/** SLA de referencia para la barra de progreso (24 h). */
export const COLA_MEDICA_SLA_MS = 24 * 60 * 60 * 1000;

const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;

const TIER_DEFS = {
  fresh: {
    id: 'fresh',
    label: 'Recién asignado',
    accent: '#059669',
    soft: 'rgba(5,150,105,.1)',
    ring: 'rgba(16,185,129,.32)',
    text: '#047857',
    border: 'rgba(16,185,129,.38)',
    headerBg: 'linear-gradient(180deg, rgba(236,253,245,.95) 0%, #ffffff 100%)',
    shadow: 'rgba(5,150,105,.12)',
    showTierLabel: false
  },
  caution: {
    id: 'caution',
    label: 'En espera',
    accent: '#ca8a04',
    soft: 'rgba(254,240,138,.14)',
    ring: 'rgba(202,138,4,.34)',
    text: '#92400e',
    border: 'rgba(202,138,4,.36)',
    headerBg: 'linear-gradient(180deg, rgba(254,249,195,.65) 0%, #fffef7 52%, #ffffff 100%)',
    shadow: 'rgba(202,138,4,.1)',
    showTierLabel: false
  },
  warning: {
    id: 'warning',
    label: 'Demorado',
    accent: '#ea580c',
    soft: 'rgba(249,115,22,.16)',
    ring: 'rgba(234,88,12,.45)',
    text: '#9a3412',
    border: 'rgba(234,88,12,.55)',
    headerBg: 'linear-gradient(180deg, rgba(255,237,213,.95) 0%, #fffbf7 50%, #ffffff 100%)',
    shadow: 'rgba(234,88,12,.18)',
    showTierLabel: true
  },
  critical: {
    id: 'critical',
    label: 'Muy demorado',
    accent: '#dc2626',
    soft: 'rgba(220,38,38,.1)',
    ring: 'rgba(248,113,113,.4)',
    text: '#b91c1c',
    border: 'rgba(248,113,113,.45)',
    headerBg: 'linear-gradient(180deg, rgba(254,242,242,.98) 0%, #ffffff 100%)',
    shadow: 'rgba(220,38,38,.16)',
    showTierLabel: true
  }
};

/** < 1 h verde · < 24 h amarillo · 1 a <3 días naranja · ≥3 días rojo */
export function pickColaTierByWaitMs(waitMs) {
  const ms = Math.max(0, Number(waitMs) || 0);
  if (ms < MS_HOUR) return TIER_DEFS.fresh;
  if (ms < MS_DAY) return TIER_DEFS.caution;
  if (ms < 3 * MS_DAY) return TIER_DEFS.warning;
  return TIER_DEFS.critical;
}

function parseTs(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const s = String(value).trim();
  if (!s) return null;
  const d = new Date(s);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

/** Momento en que el pedido quedó en cola del médico. */
export function getColaMedicaSinceMs(pedido) {
  const candidates = [
    pedido?.fecha_derivacion_medica,
    pedido?.fecha_en_cola_medica,
    pedido?.updated_at
  ];
  for (const c of candidates) {
    const t = parseTs(c);
    if (t != null) return t;
  }
  if (String(pedido?.estado || '') === 'pendiente_aprobacion_medica') {
    return parseTs(pedido?.fecha_solicitud);
  }
  return null;
}

export function getColaMedicaWaitMs(pedido, nowMs = Date.now()) {
  const since = getColaMedicaSinceMs(pedido);
  if (since == null) return 0;
  return Math.max(0, nowMs - since);
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Texto humano: "Esperando 2 h 15 min". */
export function formatColaMedicaDuration(waitMs) {
  const ms = Math.max(0, Number(waitMs) || 0);
  if (ms < 60 * 1000) return 'Recién asignado';
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 60) return `Esperando ${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 24) return m ? `Esperando ${h} h ${pad2(m)} min` : `Esperando ${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `Esperando ${d} d ${rh} h` : `Esperando ${d} d`;
}

/** Tooltip legible: tiempo en cola + fecha de derivación/asignación. */
export function formatColaMedicaTooltip(pedido, nowMs = Date.now()) {
  const meta = getColaMedicaMeta(pedido, nowMs);
  const sinceMs = getColaMedicaSinceMs(pedido);
  let fechaLabel = 'Sin fecha registrada';
  if (sinceMs != null) {
    try {
      fechaLabel = new Date(sinceMs).toLocaleString('es-AR', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (_) { /* ignore */ }
  }
  const origen = pedido?.fecha_derivacion_medica
    ? 'Derivado al médico'
    : pedido?.fecha_en_cola_medica
      ? 'Asignado a cola médica'
      : 'Tomado desde solicitud';
  const tierPart = meta.showTierLabel ? ` · ${meta.tierLabel}` : '';
  return `${meta.durationLabel}${tierPart} — ${origen}: ${fechaLabel}`;
}

export function getColaMedicaMeta(pedido, nowMs = Date.now()) {
  const waitMs = getColaMedicaWaitMs(pedido, nowMs);
  const tier = pickColaTierByWaitMs(waitMs);
  const sinceMs = getColaMedicaSinceMs(pedido);
  const progress = Math.min(1, waitMs / COLA_MEDICA_SLA_MS);
  const slaBreached = waitMs >= COLA_MEDICA_SLA_MS;
  return {
    waitMs,
    tier: tier.id,
    tierLabel: tier.label,
    showTierLabel: tier.showTierLabel,
    accent: tier.accent,
    soft: tier.soft,
    ring: tier.ring,
    text: tier.text,
    border: tier.border,
    headerBg: tier.headerBg,
    shadow: tier.shadow,
    durationLabel: formatColaMedicaDuration(waitMs),
    progress,
    slaBreached,
    pulse: tier.id === 'critical',
    sinceMs,
    sinceIso: sinceMs != null ? new Date(sinceMs).toISOString() : null
  };
}

/** Estilos de borde / header para cards pendientes médico. */
export function getColaMedicaCardVisual(pedido, nowMs = Date.now()) {
  const meta = getColaMedicaMeta(pedido, nowMs);
  return {
    tier: meta.tier,
    accent: meta.accent,
    soft: meta.soft,
    ring: meta.ring,
    text: meta.text,
    border: meta.border,
    headerBg: meta.headerBg,
    shadow: meta.shadow,
    insetShadow: `inset 3px 0 0 ${meta.accent}`,
    cardBorder: `1px solid ${meta.border}`,
    cardBoxShadow: `0 1px 3px ${meta.shadow}, inset 3px 0 0 ${meta.accent}`
  };
}

/** Peor demora del grupo (para header de paciente con varios pedidos). */
export function getGroupColaCardVisual(pedidos, nowMs = Date.now()) {
  const list = Array.isArray(pedidos) ? pedidos : [];
  let maxWait = 0;
  list.forEach((p) => {
    if (String(p?.estado || '') !== 'pendiente_aprobacion_medica') return;
    maxWait = Math.max(maxWait, getColaMedicaWaitMs(p, nowMs));
  });
  const tier = pickColaTierByWaitMs(maxWait);
  return {
    tier: tier.id,
    accent: tier.accent,
    border: tier.border,
    headerBg: tier.headerBg,
    shadow: tier.shadow,
    insetShadow: `inset 3px 0 0 ${tier.accent}`,
    cardBorder: `1px solid ${tier.border}`,
    cardBoxShadow: `0 1px 3px ${tier.shadow}, inset 3px 0 0 ${tier.accent}`
  };
}

export function summarizeColaMedica(pedidos, nowMs = Date.now()) {
  const list = Array.isArray(pedidos) ? pedidos : [];
  let caution = 0;
  let warning = 0;
  let critical = 0;
  let maxWaitMs = 0;
  let maxPedido = null;
  list.forEach((p) => {
    if (String(p?.estado || '') !== 'pendiente_aprobacion_medica') return;
    const meta = getColaMedicaMeta(p, nowMs);
    if (meta.tier === 'caution') caution += 1;
    if (meta.tier === 'warning') warning += 1;
    if (meta.tier === 'critical') critical += 1;
    if (meta.waitMs >= maxWaitMs) {
      maxWaitMs = meta.waitMs;
      maxPedido = p;
    }
  });
  return {
    caution,
    warning,
    critical,
    /** @deprecated use warning */
    attention: warning + caution,
    /** @deprecated */
    urgent: 0,
    alertCount: caution + warning + critical,
    maxWaitMs,
    maxDurationLabel: maxWaitMs ? formatColaMedicaDuration(maxWaitMs) : null,
    maxPaciente: maxPedido?.paciente || null
  };
}
